import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardMatrix, getDashboardIncassatoReale } from "@/lib/api";
import { openPrintWindow } from "@/lib/printWindow";

export default function Dashboard() {
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState(null);
  const [incassato, setIncassato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("scaduto");
  const [daData, setDaData] = useState("");
  const [aData, setAData] = useState("");
  const [daDataInc, setDaDataInc] = useState("");
  const [aDataInc, setADataInc] = useState("");
  const [soglia, setSoglia] = useState(80);
  const printRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [m, inc] = await Promise.all([
        getDashboardMatrix({ ...(daData?{da_data:daData}:{}), ...(aData?{a_data:aData}:{}) }),
        getDashboardIncassatoReale({ ...(daDataInc?{da_data:daDataInc}:{}), ...(aDataInc?{a_data:aDataInc}:{}) }),
      ]);
      setMatrix(m.data); setIncassato(inc.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [daData, aData, daDataInc, aDataInc]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtNum = (v) => { if(!v||v===0) return "-"; return new Intl.NumberFormat("it-IT",{style:"currency",currency:"EUR"}).format(v); };
  const pct = (p,a) => { if(!a||a===0) return null; return Math.round((p/a)*100); };

  const handlePrint = () => {
    const content = printRef.current; if(!content) return;
    const title = tab==="scaduto"?"Scaduto / Incassato":"Incassato Reale";
    const tableHtml = content.querySelector("table")?.outerHTML || "";
    const html = `<!DOCTYPE html><html><head><title>${title}</title><style>body{font-family:Arial;font-size:11px;margin:16px}h2{margin:0 0 8px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:3px 6px;text-align:right}th{background:#e8e8e8;font-size:10px}.cau{text-align:left;background:#ffe;font-weight:600}.pct-low{color:red;font-weight:700}.tot{background:#ddd;font-weight:700}.info{color:#666;font-size:10px;margin-bottom:12px}</style></head><body><h2>${title}</h2><p class="info">Stampato il ${new Date().toLocaleDateString("it-IT")}</p>${tableHtml}</body></html>`;
    openPrintWindow(html, { autoPrint: true });
  };

  return (
    <div data-testid="dashboard-page">
      <div className="sw-page-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1>Dashboard</h1>
          <div className="sw-breadcrumb">Home</div>
        </div>
        <button
          data-testid="btn-open-analytics"
          onClick={() => navigate("/analytics_dashboard")}
          title="Apre la nuova pagina Analytics con grafici pagamenti, agevolazioni, iscrizioni extra e composizione classi"
          style={{
            padding: "10px 18px",
            background: "linear-gradient(135deg, #1a237e 0%, #6a1b9a 100%)",
            color: "white",
            border: "none",
            borderRadius: 6,
            fontWeight: 900,
            fontSize: 14,
            letterSpacing: 0.3,
            cursor: "pointer",
            boxShadow: "0 3px 8px rgba(26,35,126,0.35)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginRight: 20,
          }}>
          📊 Apri Analytics Dashboard
          <span style={{ fontSize: 16 }}>›</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="sw-tabs">
        <div data-testid="tab-scaduto" className={`sw-tab ${tab==="scaduto"?"active":""}`} onClick={()=>setTab("scaduto")}>Scaduto / Incassato</div>
        <div data-testid="tab-incassato" className={`sw-tab ${tab==="incassato"?"active":""}`} onClick={()=>setTab("incassato")}>Incassato Reale</div>
      </div>

      {/* Filters */}
      <div style={{margin:"0 20px",border:"1px solid #ccc",borderTop:"none",background:"white",padding:12}}>
        <div className="sw-filter-row">
          {tab==="scaduto" ? <>
            <span className="sw-filter-label">scad. da:</span>
            <input data-testid="da-data-filter" type="text" placeholder="gg/mm/aaaa" value={daData} onChange={e=>setDaData(e.target.value)} className="sw-filter-input" />
            <span className="sw-filter-label">scad. a:</span>
            <input data-testid="a-data-filter" type="text" placeholder="gg/mm/aaaa" value={aData} onChange={e=>setAData(e.target.value)} className="sw-filter-input" />
            {(daData||aData)&&<button className="sw-filter-btn" onClick={()=>{setDaData("");setAData("");}}>Pulisci</button>}
            <span style={{width:20}}/>
            <span className="sw-filter-label">soglia %:</span>
            <input data-testid="soglia-input" type="number" min={0} max={100} value={soglia} onChange={e=>setSoglia(parseInt(e.target.value)||0)} className="sw-filter-input" style={{width:60}} />
          </> : <>
            <span className="sw-filter-label">pagam. da:</span>
            <input data-testid="da-data-inc-filter" type="text" placeholder="gg/mm/aaaa" value={daDataInc} onChange={e=>setDaDataInc(e.target.value)} className="sw-filter-input" />
            <span className="sw-filter-label">pagam. a:</span>
            <input data-testid="a-data-inc-filter" type="text" placeholder="gg/mm/aaaa" value={aDataInc} onChange={e=>setADataInc(e.target.value)} className="sw-filter-input" />
            {(daDataInc||aDataInc)&&<button className="sw-filter-btn" onClick={()=>{setDaDataInc("");setADataInc("");}}>Pulisci</button>}
          </>}
          <div style={{flex:1}}/>
          <button data-testid="print-btn" className="sw-filter-btn" onClick={handlePrint}>🖨 Stampa PDF</button>
        </div>
        <div style={{fontSize:11,color:"#888",marginTop:4}}>
          {tab==="scaduto" ? "Addebiti = importi in scadenza nel periodo | Incassato = importi effettivamente pagati | % sotto soglia in rosso"
            : "Totale importi effettivamente pagati nel periodo, a prescindere dalla data di scadenza"}
        </div>
      </div>

      {/* Matrix */}
      <div ref={printRef} style={{margin:"0 20px 20px",border:"1px solid #ccc",borderTop:"none",background:"white",overflow:"auto"}}>
        {loading ? <div className="sw-loading">Caricamento...</div>
        : tab==="scaduto" && matrix?.rows?.length > 0 ? <MatrixTbl data={matrix} fmtNum={fmtNum} pct={pct} soglia={soglia} mode="scaduto"/>
        : tab==="incassato" && incassato?.rows?.length > 0 ? <MatrixTbl data={incassato} fmtNum={fmtNum} pct={pct} soglia={soglia} mode="incassato"/>
        : <div className="sw-empty"><p>Nessun dato disponibile</p><p style={{fontSize:11}}>Sincronizza i dati da Segremat Web</p></div>}
      </div>
    </div>
  );
}

function MatrixTbl({ data, fmtNum, pct, soglia, mode }) {
  const isSc = mode==="scaduto";
  return (
    <table className="sw-table" style={{fontSize:11}}>
      <thead>
        <tr>
          <th style={{textAlign:"left",minWidth:40}}>Cod</th>
          <th style={{textAlign:"left",minWidth:140}}>Causale</th>
          {(data.gruppi||[]).map(g => isSc ? (
            <React.Fragment key={g.nome}>
              <th colSpan={3} style={{textAlign:"center",background:"#0066cc",color:"white",fontSize:12}}>{g.nome}<br/><span style={{fontSize:9,fontWeight:400}}>{g.settori.join("+")}</span></th>
            </React.Fragment>
          ) : (
            <th key={g.nome} style={{textAlign:"center",background:"#0066cc",color:"white",fontSize:12}}>{g.nome}<br/><span style={{fontSize:9,fontWeight:400}}>{g.settori.join("+")}</span></th>
          ))}
          <th colSpan={isSc?3:1} style={{textAlign:"center",background:"#006600",color:"white",fontSize:12}}>TOTALE</th>
        </tr>
        {isSc && <tr>
          <th/><th/>
          {(data.gruppi||[]).map(g=><React.Fragment key={g.nome}><th style={{textAlign:"center",fontSize:10}}>Addeb.</th><th style={{textAlign:"center",fontSize:10,color:"#006600"}}>Incass.</th><th style={{textAlign:"center",fontSize:10,color:"#0066cc"}}>%</th></React.Fragment>)}
          <th style={{textAlign:"center",fontSize:10}}>Addeb.</th><th style={{textAlign:"center",fontSize:10,color:"#006600"}}>Incass.</th><th style={{textAlign:"center",fontSize:10,color:"#0066cc"}}>%</th>
        </tr>}
        {!isSc && <tr><th/><th/>{(data.gruppi||[]).map(g=><th key={g.nome} style={{textAlign:"center",fontSize:10,color:"#006600"}}>Incassato</th>)}<th style={{textAlign:"center",fontSize:10,color:"#006600"}}>Incassato</th></tr>}
      </thead>
      <tbody>
        {data.rows.map((row,idx)=>(
          <tr key={row.causale} style={{background:idx%2===0?"white":"#f9f9f9"}}>
            <td className="cau" style={{color:"#888",fontFamily:"Consolas",fontSize:10}}>{row.cau_code}</td>
            <td className="cau">{row.causale}</td>
            {isSc ? <>
              {(data.gruppi||[]).map(g=>{const c=row.gruppi?.[g.nome]||{addebiti:0,pagato:0};const p=pct(c.pagato,c.addebiti);return(
                <React.Fragment key={g.nome}>
                  <td style={{textAlign:"right"}}>{fmtNum(c.addebiti)}</td>
                  <td style={{textAlign:"right",color:"#006600"}}>{fmtNum(c.pagato)}</td>
                  <td style={{textAlign:"center",fontWeight:700,color:p!==null&&p<soglia?"#cc0000":"#0066cc"}}>{p!==null?p+"%":"-"}</td>
                </React.Fragment>
              );})}
              {(()=>{const p=pct(row.totale.pagato,row.totale.addebiti);return<>
                <td style={{textAlign:"right",fontWeight:700,background:"#f0f0f0"}}>{fmtNum(row.totale.addebiti)}</td>
                <td style={{textAlign:"right",fontWeight:700,color:"#006600",background:"#f0f0f0"}}>{fmtNum(row.totale.pagato)}</td>
                <td style={{textAlign:"center",fontWeight:700,background:"#f0f0f0",color:p!==null&&p<soglia?"#cc0000":"#0066cc"}}>{p!==null?p+"%":"-"}</td>
              </>;})()}
            </> : <>
              {(data.gruppi||[]).map(g=><td key={g.nome} style={{textAlign:"right",color:"#006600"}}>{fmtNum(row.gruppi?.[g.nome]||0)}</td>)}
              <td style={{textAlign:"right",fontWeight:700,color:"#006600",background:"#f0f0f0"}}>{fmtNum(row.totale)}</td>
            </>}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{fontWeight:700,background:"#e0e0e0"}}>
          <td/><td style={{textAlign:"left"}}>TOTALE</td>
          {isSc ? <>
            {(data.gruppi||[]).map(g=>{const t=data.totali_gruppo?.[g.nome]||{addebiti:0,pagato:0};const p=pct(t.pagato,t.addebiti);return(
              <React.Fragment key={g.nome}>
                <td style={{textAlign:"right"}}>{fmtNum(t.addebiti)}</td>
                <td style={{textAlign:"right",color:"#006600"}}>{fmtNum(t.pagato)}</td>
                <td style={{textAlign:"center",color:p!==null&&p<soglia?"#cc0000":"#0066cc"}}>{p!==null?p+"%":"-"}</td>
              </React.Fragment>
            );})}
            {(()=>{const p=pct(data.grand_total.pagato,data.grand_total.addebiti);return<>
              <td style={{textAlign:"right",background:"#ccc"}}>{fmtNum(data.grand_total.addebiti)}</td>
              <td style={{textAlign:"right",color:"#006600",background:"#ccc"}}>{fmtNum(data.grand_total.pagato)}</td>
              <td style={{textAlign:"center",background:"#ccc",color:p!==null&&p<soglia?"#cc0000":"#0066cc"}}>{p!==null?p+"%":"-"}</td>
            </>;})()}
          </> : <>
            {(data.gruppi||[]).map(g=><td key={g.nome} style={{textAlign:"right",color:"#006600"}}>{fmtNum(data.totali_gruppo?.[g.nome]||0)}</td>)}
            <td style={{textAlign:"right",color:"#006600",background:"#ccc"}}>{fmtNum(data.grand_total)}</td>
          </>}
        </tr>
      </tfoot>
    </table>
  );
}
